import React, { useEffect, useRef } from "react";
import { Outlet, useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { ContactSidebarForm } from "./ContactSidebarForm";
import { useSettings } from "../hooks/useSettings";

export const Layout = ({ isLoading }) => {
  const { settings } = useSettings();
  const location = useLocation();
  const isEventsPage = location.pathname === "/events";
  const isLoginPage = location.pathname === "/login";
  // The /Test page previews the "left wall" standard: its text is boxed, and the
  // logo and HOME button line up with that text. Only /Test opts in for now,
  // since the other pages aren't boxed the same way yet.
  const alignHeaderWithBoxedPage = location.pathname.toLowerCase() === "/test";

  // Set document title from community name (fetched via community ID from env/header)
  useEffect(() => {
    const communityName = settings?.clubName || settings?.shortName || "Community";
    document.title = communityName;
  }, [settings?.clubName, settings?.shortName]);

  // The header is sticky, so anything else that wants to stick has to sit below
  // it. Its height changes with the viewport (the nav row wraps, the logo block
  // shrinks) and with the admin's nav settings, so publish the measured value as
  // --site-header-height rather than hard-coding an offset in each consumer.
  const headerRef = useRef(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const publish = () =>
      document.documentElement.style.setProperty(
        "--site-header-height",
        `${Math.round(el.getBoundingClientRect().height)}px`
      );
    publish();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", publish);
      return () => window.removeEventListener("resize", publish);
    }
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, [settings]);

  // Show sidebar on all pages EXCEPT Home (/), Contact (/contact) and Thank You (/thank-you)
  const isHomePage = location.pathname === "/";
  const isContactPage = location.pathname === "/contact";
  const isThankYouPage = location.pathname === "/thank-you";
  const showSidebar = !isHomePage && !isContactPage && !isThankYouPage && !isLoginPage;

  if (isLoading) {
    return (
      <div className="flex h-screen justify-center items-center">
        <i className="pi pi-spin pi-spinner text-red-700" style={{ fontSize: '2rem' }}></i>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col min-h-screen"
      style={{ fontFamily: "Tahoma" }}
    >
      <div className="sticky top-0 z-50" ref={headerRef}>
        <Header alignWithBoxedPage={alignHeaderWithBoxedPage} />
      </div>

      {/* Main content grows to push footer down */}
      <main className={`flex-grow ${isEventsPage || isLoginPage ? "" : "container mx-auto px-4 pt-2 pb-4"}`}>
        {showSidebar ? (
          // Desktop (lg+): the contact form sits in a 320px right-hand sidebar.
          // Mobile/tablet (< lg): the row becomes a column so the form drops to
          // the BOTTOM of the page (below the main content) instead of being
          // hidden as it was before.
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 min-w-0">
              <Outlet />
            </div>
            <aside className="w-full lg:w-80 lg:flex-shrink-0">
              <div className="lg:sticky" style={{ top: "80px" }}>
                <ContactSidebarForm />
              </div>
            </aside>
          </div>
        ) : (
          <Outlet />
        )}
      </main>

      <Footer />
    </div>
  );
};
